// frontend/src/components/ShopBar.jsx
//
// A slim row of shortcuts that sits under the header on phones only.
//
// ── WHY IT EXISTS ─────────────────────────────────────────────────────────
//
// On mobile the whole nav collapses into a burger. Someone landing on the
// homepage sees a logo, a menu icon and a hero — nothing that says this site
// sells air conditioners you can buy right now. They have to tap the burger
// to find out, and most never will.
//
// Indian e-commerce solves this with a persistent chip row under the header:
// Flipkart, Myntra and Blinkit all do it. The chips are the entry points, no
// tap required to discover them.
//
// Desktop already shows the full nav, so this is lg:hidden.
//
// ── STICKY, NOT FIXED ─────────────────────────────────────────────────────
//
// The header is fixed with h-16, and the page wrapper carries pt-16 to clear
// it. A second fixed bar would need that padding increased and would overlap
// the hero until it was. Sticky at top-16 sits in normal flow, pins itself
// directly under the header on scroll, and needs no change to the wrapper.

import { Link } from 'react-router-dom';

const ITEMS = [
  { to: '/daikin-ac-price-list-delhi', label: 'Buy AC', primary: true, icon: 'cart' },
  { to: '/compare-ac', label: 'Compare', icon: 'compare' },
  { to: '/daikin-vrv-systems', label: 'VRV Systems', icon: 'building' },
  { to: '/ac-calculator', label: 'AC Calculator', icon: 'ruler' },
  { to: '/clearance-ac-sale', label: 'Clearance', icon: 'tag' },
];

// Inline SVG rather than lucide-react: these render inside a sticky bar on
// every page load, and four icon imports is weight this bar does not need.
// Also avoids the icon-rename breakage that took the build down before.
const ICONS = {
  cart: <><path d="M3 3h2l2.6 12.4a1 1 0 001 .8h9.7a1 1 0 001-.8L21 7H6" strokeLinecap="round" strokeLinejoin="round" /><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /></>,
  building: <><rect x="3" y="3" width="8" height="18" rx="1" /><rect x="13" y="8" width="8" height="13" rx="1" /><path d="M6 7h2M6 11h2M6 15h2M16 12h2M16 16h2" strokeLinecap="round" /></>,
  ruler: <><rect x="2" y="8" width="20" height="8" rx="1.5" /><path d="M7 8v3M12 8v4M17 8v3" strokeLinecap="round" /></>,
  compare: <><rect x="3" y="4" width="7" height="16" rx="1.5" /><rect x="14" y="4" width="7" height="16" rx="1.5" /><path d="M6.5 9h0M17.5 9h0" strokeLinecap="round" /></>,
  tag: <><path d="M12.6 2.6A2 2 0 0011.2 2H4a2 2 0 00-2 2v7.2a2 2 0 00.6 1.4l8.7 8.7a2.4 2.4 0 003.4 0l6.6-6.6a2.4 2.4 0 000-3.4z" strokeLinecap="round" strokeLinejoin="round" /><circle cx="7.5" cy="7.5" r="1.2" /></>,
};

export default function ShopBar() {
  return (
    <div className="sticky top-16 z-40 border-b border-gray-200 bg-white/95 backdrop-blur-sm lg:hidden">
      {/* Horizontal scroll rather than wrapping: a second row would push the
          hero down on every page. scrollbar-none keeps it clean on Android,
          which shows a bar by default. */}
      <div className="flex gap-2 overflow-x-auto px-3 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {ITEMS.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className={
              it.primary
                ? 'buy-ac-cta flex shrink-0 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-800 transition active:border-blue-400 active:text-blue-700'
                : 'flex shrink-0 items-center gap-1.5 rounded-full border border-gray-300 px-3.5 py-2 text-xs font-medium text-gray-700 transition active:border-blue-400 active:text-blue-700'
            }
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {ICONS[it.icon]}
            </svg>
            {it.label}
          </Link>
        ))}
      </div>
    </div>
  );
}